/* eslint-disable jsx-a11y/label-has-associated-control */
import { ArrowBackIos, Cancel, Save } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Grid, Tooltip } from '@mui/material';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import WarningIcon from '@mui/icons-material/Warning';
import { Header } from '../../../../components/header/header';
import {
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../../../components/layout/main/mainStyles';
import Strings from '../../../../i18n';
import { ButtonIconStyled } from '../../../locations/allLocationsList/allLocationListStyles';
import {
  ItemGridAddEvent,
  LoaderIndicator,
  StyledTitleLabelAddEvent,
  WhenButtonSelector
} from './addEventStyles';
import { LightZoneSelector } from '../../../../components/lightsZones/lightZoneSelector';
import { ILightZone } from '../../../../types/Lights/lightZoneInterfaces';
import { eventTypes } from '../../../../constants/eventEnums';
import { LightsZonesOff } from './offEvent/offEvent';
import {
  IAddEvent,
  IDateType,
  ITimeType
} from '../../../../types/events/eventsInterface';
import { EventUtils, eventDefaultSettings } from '../../../../utils/eventUtils';
import { PickEventTime } from '../../../../components/events/pickEventTime/pickEventTime';
import { PickEventDate } from '../../../../components/events/pickEventDate/pickEventDate';
import { PickEventDays } from '../../../../components/events/pickEventDays/pickEventDays';
import {
  usePostEventColorMutation,
  usePostEventOffMutation,
  usePostEventSceneMutation,
  usePutEventColorMutation,
  usePutEventOffMutation,
  usePutEventSceneMutation
} from '../../../../services/events/eventService';
import { SceneEvent } from './sceneEvent/sceneEvent';
import { OnColorEvent } from './onColorEvent/onColorEvent';
import './addEventCssStyles.css';
import { CommandsTypesItemEnum } from '../../../../constants/commonEnums';
import { SingleGroupSelector } from '../../../../components/groups/singleGroupSelector';
import { IGroup } from '../../../../types/Groups/groupsInterfaces';
import {
  eventToEdit,
  setEditEvent,
  isUpdateEvent,
  setIsUpdateEvent
} from '../../../../store/slices/locations/location';
import { useGetGroupsByLocationIdMutation } from '../../../../services/groups/groupsService';
import { useGetSceneListByLocationMutation } from '../../../../services/scenes/sceneService';

export const AddEventTemplate = () => {
  const [
    postOffEvent,
    { isSuccess: isSuccessPostOff, isLoading: isLoadingOffEvent, isError }
  ] = usePostEventOffMutation();

  const [
    postSceneEvent,
    {
      isSuccess: isSuccessPostScene,
      isLoading: isLoadingSceneEvent,
      isError: isErrorSceneEvent
    }
  ] = usePostEventSceneMutation();

  const [
    postColorEvent,
    {
      isSuccess: isSuccessPostColor,
      isLoading: isLoadingColorEvent,
      isError: isErrorColorEvent
    }
  ] = usePostEventColorMutation();

  const [
    putOffEvent,
    {
      isSuccess: isSuccessPutOff,
      isLoading: isLoadingPutOffEvent,
      isError: isErrorPutOffEvent
    }
  ] = usePutEventOffMutation();

  const [
    putSceneEvent,
    {
      isSuccess: isSuccessPutScene,
      isLoading: isLoadingPutSceneEvent,
      isError: isErrorPutSceneEvent
    }
  ] = usePutEventSceneMutation();

  const [
    putColorEvent,
    {
      isSuccess: isSuccessPutColor,
      isLoading: isLoadingPutColorEvent,
      isError: isErrorPutColorEvent
    }
  ] = usePutEventColorMutation();

  const [getGroups, { isSuccess: successGroups, data: groups }] =
    useGetGroupsByLocationIdMutation();
  const [getScenes, { isSuccess: successScenes, data: scenes }] =
    useGetSceneListByLocationMutation();

  const { locationId, eventType } = useParams();
  const initialEventState = eventDefaultSettings;
  const dispatch = useDispatch();
  const eventEdit = useSelector(eventToEdit);
  const isEventUpdated = useSelector(isUpdateEvent);

  const [event, setEvent] = useState<IAddEvent>(initialEventState);
  const [eventName, setEventName] = useState(eventDefaultSettings.name);
  const [submitted, setSubmitted] = useState(false);
  const [selectedScene, setSelectedScene] = useState<any>(null);
  const [selectedColorAndBrightness, setSelectedColorAndBrightness] = useState<{
    brightness: number;
    colorId: number;
  }>({
    brightness:
      eventEdit && eventEdit.lightBrightnessId
        ? eventEdit.lightBrightnessId
        : 5,
    colorId: eventEdit && eventEdit.lightColorId ? eventEdit.lightColorId : 1
  });
  const [showEventTimePicker, setShowEventTimePicker] = useState(false);
  const [showEventDatePicker, setShowEventDatePicker] = useState(false);
  const [showEventDaysPicker, setShowEventDaysPicker] = useState(false);
  const [expiring, setExpiring] = useState(event.expiring);

  const navigate = useNavigate();
  const [selectedLightZones, setSelectedLightZones] = useState<
    {
      id: number;
      name: string;
      isZone: boolean;
    }[]
  >([]);

  const [selectedGroup, setSelectedGroup] = useState<IGroup>();
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const resetEventState = () => {
    setEvent(initialEventState);
    setEventName(initialEventState.name);
    setSelectedLightZones([]);
    setSelectedScene(null);
    setShowEventTimePicker(false);
    setShowEventDatePicker(false);
    setShowEventDaysPicker(false);
    setSubmitted(false);
  };
  const handleExpiringChange = (e: any) => {
    setExpiring(e.checked);
    event.expiring = e.checked;
  };

  useEffect(() => {
    if (isSuccessPostOff || isSuccessPostScene || isSuccessPostColor) {
      navigate(`/locations/${locationId}/devices/schedules`);
      toast.success(Strings.events.eventAddedSuccessfully);
    }
  }, [isSuccessPostOff, isSuccessPostScene, isSuccessPostColor]);

  useEffect(() => {
    if (isSuccessPutOff || isSuccessPutScene || isSuccessPutColor) {
      navigate(`/locations/${locationId}/devices/schedules`);
      toast.success(Strings.events.eventUpdatedSuccessfully);
    }
  }, [isSuccessPutOff, isSuccessPutScene, isSuccessPutColor]);

  useEffect(() => {
    if (isError || isErrorColorEvent || isErrorSceneEvent) {
      toast.error(Strings.events.errorEvent);
    }
  }, [isError, isErrorColorEvent, isErrorSceneEvent]);

  useEffect(() => {
    if (isErrorPutOffEvent || isErrorPutColorEvent || isErrorPutSceneEvent) {
      toast.error(Strings.events.errorUpdateEvent);
    }
  }, [isErrorPutOffEvent, isErrorPutColorEvent, isErrorPutSceneEvent]);
  useEffect(() => {
    if (locationId) {
      getScenes(parseInt(locationId, 10));
      getGroups(parseInt(locationId, 10));
    }
    return () => {
      dispatch(setEditEvent(null));
      dispatch(setIsUpdateEvent(false));
    };
  }, []);

  const loadEventValues = () => {
    if (eventEdit) {
      const updatedTime = EventUtils.getEventTime({
        timeType: eventEdit.timeType,
        hour: isEventUpdated
          ? eventEdit.hour
          : EventUtils.addOneHour(eventEdit.hour),
        minute: eventEdit.minute,
        offsetMinutes: isEventUpdated
          ? eventEdit.offsetMinutes
          : eventEdit.offsetMinutes + 60
      });
      const updatedDate = EventUtils.getEventDate(eventEdit);
      const findedGroup = groups?.find(
        (group) => group.groupId === eventEdit.groupId
      );
      const findedScene = scenes?.find(
        (scene) => scene.id === eventEdit.sceneId
      );

      if (findedScene) {
        setSelectedScene(findedScene);
      }
      if (findedGroup) {
        setSelectedGroup(findedGroup);
      }
      setExpiring(eventEdit.expiring || false);
      setSelectedDate(updatedDate);
      setSelectedTime(updatedTime);
      setSelectedLightZones(
        eventEdit.lightIds?.map((lightZone) => ({
          id: lightZone.lightZoneId,
          name: lightZone.lightZoneName,
          isZone: lightZone.isZone
        })) || []
      );
      setEventName(eventEdit.name);
      setEvent({
        ...(eventEdit as unknown as IAddEvent),
        eventId: isEventUpdated ? eventEdit.eventId : undefined,
        hour: isEventUpdated
          ? eventEdit.hour
          : EventUtils.addOneHour(eventEdit.hour),
        eventTime: undefined,
        startDate: undefined,
        stopDate: undefined,
        offsetMinutes: isEventUpdated
          ? eventEdit.offsetMinutes
          : eventEdit.offsetMinutes + 60
      });
    }
  };

  useEffect(() => {
    if (eventEdit && successScenes && successGroups) {
      loadEventValues();
    }
  }, [eventEdit, successScenes, successGroups]);

  const renderEventTypeSwitch = () => {
    if (!eventType) {
      return <p />;
    }
    switch (+eventType) {
      case eventTypes.OFF_EVENT:
        return <LightsZonesOff />;
      case eventTypes.COLOR_EVENT:
        return (
          <OnColorEvent
            emitSelectionColor={(selection) => {
              setSelectedColorAndBrightness(selection);
            }}
            status={{
              colorId: selectedColorAndBrightness.colorId,
              brightness: selectedColorAndBrightness.brightness
            }}
          />
        );
      case eventTypes.SCENE_EVENT:
        return (
          <SceneEvent
            emitSelectedScene={(scene) => setSelectedScene(scene)}
            scene={selectedScene}
          />
        );
      default:
        return <p />;
    }
  };

  const setTimeEvent = (data?: ITimeType) => {
    const updatedTime = data
      ? EventUtils.getEventTime({
          timeType: data.timeType,
          hour: data.hour,
          minute: data.minute,
          offsetMinutes: data.offsetMinutes
        })
      : EventUtils.getEventTime({
          timeType: event.timeType,
          hour: event.hour,
          minute: event.minute,
          offsetMinutes: event.offsetMinutes
        });
    setSelectedTime(updatedTime);

    setEvent((prevEvent) => ({
      ...prevEvent,
      timeType: data?.timeType ?? prevEvent.timeType,
      hour: data?.hour ?? prevEvent.hour,
      minute: data?.minute ?? prevEvent.minute,
      offsetMinutes: data?.offsetMinutes ?? prevEvent.offsetMinutes
    }));
  };

  const setDateEvent = (data?: IDateType) => {
    const updatedDate = data
      ? EventUtils.getEventDate({
          dateType: data.dateType,
          startMonth: data.startMonth,
          startDay: data.startDay,
          endMonth: data.endMonth,
          endDay: data.endDay,
          holidayStartIndex: data.holidayStartIndex,
          holidayStartOffset: data.holidayStartOffset,
          holidayEndIndex: data.holidayEndIndex,
          holidayEndOffset: data.holidayEndOffset
        })
      : EventUtils.getEventDate(event);

    setSelectedDate(updatedDate);

    setEvent((prevEvent) => ({
      ...prevEvent,
      dateType: data?.dateType ?? prevEvent.dateType,
      startMonth: data?.startMonth ?? prevEvent.startMonth,
      startDay: data?.startDay ?? prevEvent.startDay,
      endMonth: data?.endMonth ?? prevEvent.endMonth,
      endDay: data?.endDay ?? prevEvent.endDay,
      holidayStartIndex: data?.holidayStartIndex ?? prevEvent.holidayStartIndex,
      holidayStartOffset:
        data?.holidayStartOffset ?? prevEvent.holidayStartOffset,
      holidayEndIndex: data?.holidayEndIndex ?? prevEvent.holidayEndIndex,
      holidayEndOffset: data?.holidayEndOffset ?? prevEvent.holidayEndOffset
    }));
  };

  const setDaysEvent = (data: { weekDays: number; daysOfWeek: number }) => {
    setEvent((prevEvent) => ({
      ...prevEvent,
      weekDays: data.weekDays,
      daysOfWeek: data.daysOfWeek.toString()
    }));
  };

  const getDisplayStringForSelectedDays = () => {
    return EventUtils.binaryToWeekdays(
      EventUtils.weekDaysBinToDaysOfWeekNumber(event.weekDays ?? 0)
    );
  };

  const setSelectedTargets = () => {
    if (selectedLightZones.length) {
      event.targets = selectedLightZones.map((lightZone) => ({
        id: lightZone.id,
        type: lightZone.isZone
          ? CommandsTypesItemEnum.Zone
          : CommandsTypesItemEnum.Light
      }));
    } else if (selectedGroup) {
      event.targets = [
        {
          id: selectedGroup.groupId,
          type: CommandsTypesItemEnum.Group
        }
      ];
    }
  };

  const switchToSendEventByType = () => {
    if (!eventType) {
      return <p />;
    }
    switch (+eventType) {
      case eventTypes.OFF_EVENT:
        setSelectedTargets();
        if (isEventUpdated) {
          putOffEvent(event);
        } else {
          postOffEvent(event);
        }
        return '';
      case eventTypes.COLOR_EVENT:
        setSelectedTargets();
        event.lightColorId = selectedColorAndBrightness.colorId;
        event.lightBrightnessId = selectedColorAndBrightness.brightness;
        if (isEventUpdated) {
          putColorEvent(event);
        } else {
          postColorEvent(event);
        }
        return '';
      case eventTypes.SCENE_EVENT:
        event.sceneId = selectedScene.id;
        if (isEventUpdated) {
          putSceneEvent(event);
        } else {
          postSceneEvent(event);
        }
        return <p />;
      default:
        return <p />;
    }
  };

  const addEvent = () => {
    setSubmitted(true);
    if (eventName && selectedTime && selectedDate && eventType) {
      switchToSendEventByType();
    }
  };
  useEffect(() => {
    setEvent((prevEvent) => ({
      ...prevEvent,
      name: eventName
    }));
  }, [eventName]);
  useEffect(() => {
    if (locationId && eventType && !eventEdit) {
      resetEventState();
      setEvent((prevEvent) => ({
        ...prevEvent,
        locationId: +locationId,
        eventType: +eventType
      }));
      setDateEvent();
      setTimeEvent();
    }
  }, []);
  return (
    <>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.events.addEventTitle}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              {isLoadingColorEvent ||
              isLoadingOffEvent ||
              isLoadingColorEvent ||
              isLoadingPutColorEvent ||
              isLoadingPutOffEvent ||
              isLoadingPutSceneEvent ? (
                <LoaderIndicator size={20} color="info" />
              ) : (
                <>
                  {!selectedLightZones.length && !selectedGroup && (
                    <Tooltip title={Strings.common.notTarget}>
                      <ButtonIconStyled size="small" disabled={false}>
                        <WarningIcon color="warning" />
                      </ButtonIconStyled>
                    </Tooltip>
                  )}
                  <Tooltip title={Strings.common.save}>
                    <ButtonIconStyled
                      size="small"
                      onClick={() => addEvent()}
                      disabled={false}
                    >
                      <Save />
                    </ButtonIconStyled>
                  </Tooltip>
                </>
              )}
              <Tooltip title={Strings.common.cancel}>
                <ButtonIconStyled
                  size="small"
                  onClick={() =>
                    navigate(`/locations/${locationId}/devices/schedules`)
                  }
                  disabled={false}
                >
                  <Cancel />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <Grid container>
        <ItemGridAddEvent item xs={12}>
          <StyledTitleLabelAddEvent>
            {Strings.events.eventNameLabel}
          </StyledTitleLabelAddEvent>
          <InputText
            className="w-100"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder={Strings.scenes.sceneNamePlaceholder}
          />
        </ItemGridAddEvent>
        <Divider className="mx-2" />
        <ItemGridAddEvent item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={6}>
              {renderEventTypeSwitch()}
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTitleLabelAddEvent>
                * {Strings.events.when}
              </StyledTitleLabelAddEvent>
              <Grid container>
                <Grid xs={12} item>
                  <WhenButtonSelector
                    icon="pi pi-angle-right"
                    iconPos="right"
                    label={
                      selectedTime
                        ? `${Strings.events.time} ${selectedTime}`
                        : Strings.events.setEventTime
                    }
                    onClick={() => setShowEventTimePicker(true)}
                  />
                  {submitted && !selectedTime && (
                    <div className="text-danger">
                      <small>{Strings.events.pleaseProvideEventTime}</small>
                    </div>
                  )}
                </Grid>
                <Grid xs={12} item>
                  <WhenButtonSelector
                    icon="pi pi-angle-right"
                    iconPos="right"
                    label={
                      selectedDate
                        ? `${selectedDate}`
                        : Strings.events.setEventDate
                    }
                    onClick={() => setShowEventDatePicker(true)}
                  />
                  {submitted && !selectedDate && (
                    <div className="text-danger">
                      <small>{Strings.events.pleaseProvideEventDate}</small>
                    </div>
                  )}
                </Grid>
                <Grid xs={12} item>
                  <WhenButtonSelector
                    icon="pi pi-angle-right"
                    iconPos="right"
                    label={getDisplayStringForSelectedDays()}
                    onClick={() => setShowEventDaysPicker(true)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className="btn-expiring d-flex justify-content-between">
                    <label htmlFor="binary" className="ms-2 mb-0">
                      EXPIRING EVENT
                    </label>
                    <Checkbox
                      inputId="binary"
                      checked={expiring}
                      onChange={handleExpiringChange}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ItemGridAddEvent>
        {eventType && +eventType < eventTypes.SCENE_EVENT && (
          <>
            <ItemGridAddEvent item xs={12} md={6}>
              <LightZoneSelector
                locationId={locationId ? +locationId : 0}
                selectedLightZones={selectedLightZones}
                setSelectedLightZones={setSelectedLightZones}
                disabled={!!selectedGroup}
              />
            </ItemGridAddEvent>
            <ItemGridAddEvent item xs={12} md={6}>
              <SingleGroupSelector
                locationId={locationId ? +locationId : 0}
                selectedGroup={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                disabled={!!selectedLightZones.length}
              />
            </ItemGridAddEvent>
          </>
        )}
      </Grid>
      <PickEventTime
        showEventTimePicker={showEventTimePicker}
        setShowEventTimePicker={setShowEventTimePicker}
        timeType={{
          timeType: event.timeType,
          hour: event.hour,
          minute: event.minute,
          offsetMinutes: event.offsetMinutes
        }}
        onOk={(info) => setTimeEvent(info)}
      />
      <PickEventDate
        showEventDatePicker={showEventDatePicker}
        setShowEventDatePicker={setShowEventDatePicker}
        dateType={{
          dateType: event.dateType,
          holidayEndIndex: event.holidayEndIndex,
          holidayEndOffset: event.holidayEndOffset,
          endDay: event.endDay,
          endMonth: event.endMonth,
          holidayStartIndex: event.holidayStartIndex,
          holidayStartOffset: event.holidayStartOffset,
          startDay: event.startDay,
          startMonth: event.startMonth
        }}
        onOk={(info) => setDateEvent(info)}
      />
      <PickEventDays
        showEventDaysPicker={showEventDaysPicker}
        setShowEventDaysPicker={setShowEventDaysPicker}
        daysSelected={{
          weekDays: event.weekDays ?? 127,
          daysOfWeek: +event.daysOfWeek
        }}
        onOk={(info) => setDaysEvent(info)}
      />
    </>
  );
};
